// region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Helpers to normalize MT config API payloads (v1 and v2, PascalCase or camelCase).
//
//  Copyright:    © 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
// endregion header

import MultiTenantConfigGetDto, {
  DatabaseConnection,
  IndexConnection,
  MultiTenantConfigOrganisation,
  MultiTenantConfigPostDto,
  MultiTenantConfigPutDto,
  NamedConnectionSetting,
  NamedElasticSetting,
} from "../dtos/multiTenantConfigDto";
import { getConfigValue } from "../configuration/runtimeEnv";

type AnyRecord = Record<string, unknown>;

const asRecord = (value: unknown): AnyRecord | undefined =>
  value !== null && typeof value === "object" && !Array.isArray(value) ? (value as AnyRecord) : undefined;

const pickString = (source: AnyRecord | undefined, ...keys: string[]): string | undefined => {
  if (!source) return undefined;
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string") return value;
  }
  return undefined;
};

const pickNumber = (source: AnyRecord | undefined, ...keys: string[]): number | undefined => {
  if (!source) return undefined;
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "number" && !Number.isNaN(value)) return value;
    if (typeof value === "string" && value.trim().length > 0) {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return undefined;
};

const pickArray = (source: AnyRecord | undefined, ...keys: string[]): unknown[] => {
  if (!source) return [];
  for (const key of keys) {
    const value = source[key];
    if (Array.isArray(value)) return value;
  }
  return [];
};

const parseConnectionString = (
  connectionString: string | undefined
): { server?: string; database?: string; username?: string; password?: string; port?: number } => {
  if (!connectionString) return {};

  const parts = connectionString
    .split(";")
    .map((x) => x.trim())
    .filter((x) => x.length > 0);

  const getValue = (key: string): string | undefined => {
    const segment = parts.find((part) => part.toLowerCase().startsWith(`${key.toLowerCase()}=`));
    if (!segment) return undefined;
    return segment.substring(segment.indexOf("=") + 1).trim() || undefined;
  };

  const serverValue = getValue("Server");
  let server = serverValue;
  let port: number | undefined = undefined;

  if (serverValue && serverValue.includes(",")) {
    const [host, portText] = serverValue.split(",");
    server = host?.trim() || undefined;
    const parsedPort = Number(portText?.trim());
    if (!Number.isNaN(parsedPort)) port = parsedPort;
  }

  return {
    server,
    database: getValue("Database"),
    username: getValue("User ID") ?? getValue("User Id") ?? getValue("Username"),
    password: getValue("Password"),
    port,
  };
};

const normalizeDatabaseConnection = (raw: unknown): DatabaseConnection => {
  const settings = asRecord(raw) ?? {};
  const connectionString = pickString(settings, "connectionString", "ConnectionString");
  const parsed = parseConnectionString(connectionString);

  return {
    server: pickString(settings, "server", "Server") ?? parsed.server,
    database: pickString(settings, "database", "Database") ?? parsed.database,
    username: pickString(settings, "username", "Username") ?? parsed.username,
    password: pickString(settings, "password", "Password") ?? parsed.password,
    port: pickNumber(settings, "port", "Port") ?? parsed.port,
    connectionString,
  };
};

const normalizeIndexConnection = (raw: unknown): IndexConnection => {
  const settings = asRecord(raw) ?? {};
  const nodes = pickArray(settings, "elasticNodes", "ElasticNodes").filter(
    (node): node is string => typeof node === "string"
  );

  return {
    alias: pickString(settings, "alias", "Alias"),
    username: pickString(settings, "username", "Username"),
    password: pickString(settings, "password", "Password"),
    fingerprint: pickString(settings, "fingerprint", "Fingerprint"),
    elasticNodes: nodes,
  };
};

const normalizeNamedConnectionSetting = (raw: unknown): NamedConnectionSetting | undefined => {
  const record = asRecord(raw);
  if (!record) return undefined;

  const name = pickString(record, "name", "Name");
  if (!name) return undefined;

  return {
    name,
    settings: normalizeDatabaseConnection(record.settings ?? record.Settings),
  };
};

const normalizeNamedElasticSetting = (raw: unknown): NamedElasticSetting | undefined => {
  const record = asRecord(raw);
  if (!record) return undefined;

  const name = pickString(record, "name", "Name");
  if (!name) return undefined;

  return {
    name,
    settings: normalizeIndexConnection(record.settings ?? record.Settings),
  };
};

const normalizeOrganisation = (raw: unknown): MultiTenantConfigOrganisation => {
  const org = asRecord(raw) ?? {};
  const organisationName = pickString(org, "organisationName", "OrganisationName") ?? "";

  const rawNamedConnections = pickArray(org, "namedConnectionSettings", "NamedConnectionSettings");
  const rawNamedElastic = pickArray(org, "namedElasticSettings", "NamedElasticSettings");

  const namedConnectionSettings = rawNamedConnections
    .map(normalizeNamedConnectionSetting)
    .filter((x): x is NamedConnectionSetting => !!x);

  const namedElasticSettings = rawNamedElastic
    .map(normalizeNamedElasticSetting)
    .filter((x): x is NamedElasticSetting => !!x);

  const symphonyConnectionString = pickString(org, "symphonyConnectionString", "SymphonyConnectionString");
  const elasticAlias = pickString(org, "elasticAlias", "ElasticAlias");
  const elasticNodes = pickArray(org, "elasticNodes", "ElasticNodes").filter(
    (node): node is string => typeof node === "string"
  );
  const hasV1Shape = !!symphonyConnectionString || !!elasticAlias || elasticNodes.length > 0;

  // Only treat v2 named settings as authoritative when they actually contain data.
  // APIs often include empty NamedConnectionSettings/NamedElasticSettings arrays on
  // legacy v1 organisations; those must not block conversion from symphonyConnectionString.
  const hasUsableV2Connections = namedConnectionSettings.length > 0;
  const hasUsableV2Elastic = namedElasticSettings.length > 0;

  if (hasUsableV2Connections && hasUsableV2Elastic) {
    return {
      organisationName,
      namedConnectionSettings,
      namedElasticSettings,
    };
  }

  if (hasV1Shape) {
    const parsedConnection = parseConnectionString(symphonyConnectionString);
    return {
      organisationName,
      namedConnectionSettings: hasUsableV2Connections
        ? namedConnectionSettings
        : [
            {
              name: "SearchDatabaseConnection",
              settings: {
                server: parsedConnection.server,
                database: parsedConnection.database,
                username: parsedConnection.username,
                password: parsedConnection.password,
                port: parsedConnection.port,
                connectionString: symphonyConnectionString,
              },
            },
          ],
      namedElasticSettings: hasUsableV2Elastic
        ? namedElasticSettings
        : [
            {
              name: "SearchIndexConnection",
              settings: {
                alias: elasticAlias,
                username: undefined,
                password: undefined,
                fingerprint: undefined,
                elasticNodes,
              },
            },
          ],
    };
  }

  return {
    organisationName,
    namedConnectionSettings,
    namedElasticSettings,
  };
};

/** Stable unique key for a config document in the tree / selection paths. */
export const getConfigDocumentKey = (doc: Pick<MultiTenantConfigGetDto, "id" | "name" | "appName">): string =>
  doc.id || doc.name || doc.appName;

/** Resolve a document from a tree root key (id, name, or appName). */
export const findConfigDocument = (
  docs: MultiTenantConfigGetDto[],
  key: string | undefined
): MultiTenantConfigGetDto | undefined => {
  if (!key) return undefined;
  return docs.find((doc) => doc.id === key || doc.name === key || doc.appName === key);
};

/**
 * Normalize a config document from the API into the camelCase v2 shape used by the UI.
 * Accepts v1 and v2 documents, and either PascalCase or camelCase property names.
 */
export const normalizeConfig = (doc: unknown): MultiTenantConfigGetDto => {
  const source = asRecord(doc) ?? {};
  const name = pickString(source, "name", "Name") ?? "";
  const appName = pickString(source, "appName", "AppName") || name;
  const organisations = pickArray(source, "organisations", "Organisations").map(normalizeOrganisation);

  return {
    id: pickString(source, "id", "Id") ?? "",
    configType: pickString(source, "configType", "ConfigType") ?? "MTConfigV2",
    version: pickNumber(source, "version", "Version") ?? 2,
    name,
    appName,
    created: pickString(source, "created", "Created") ?? "",
    lastUpdated: pickString(source, "lastUpdated", "LastUpdated") ?? "",
    createdBy: pickString(source, "createdBy", "CreatedBy") ?? "",
    lastUpdatedBy: pickString(source, "lastUpdatedBy", "LastUpdatedBy") ?? "",
    organisations,
  };
};

const toApiDatabaseConnection = (settings: DatabaseConnection): AnyRecord => {
  const payload: AnyRecord = {
    server: settings.server ?? "",
    database: settings.database ?? "",
    username: settings.username ?? "",
    password: settings.password ?? "",
    port: settings.port ?? 0,
  };

  if (settings.connectionString) {
    payload.connectionString = settings.connectionString;
  }

  return payload;
};

const toApiIndexConnection = (settings: IndexConnection): AnyRecord => {
  const payload: AnyRecord = {
    alias: settings.alias ?? "",
    username: settings.username ?? "",
    password: settings.password ?? "",
    elasticNodes: settings.elasticNodes ?? [],
  };

  if (settings.fingerprint) {
    payload.fingerprint = settings.fingerprint;
  }

  return payload;
};

const buildConnectionString = (settings: DatabaseConnection | undefined): string => {
  if (!settings) return "";
  if (settings.connectionString && settings.connectionString.trim().length > 0) {
    return settings.connectionString;
  }

  const server =
    settings.server && settings.port
      ? `${settings.server},${settings.port}`
      : settings.server ?? "";

  const parts = [
    server ? `Server=${server}` : undefined,
    settings.database ? `Database=${settings.database}` : undefined,
    settings.username ? `User ID=${settings.username}` : undefined,
    settings.password ? `Password=${settings.password}` : undefined,
    "Trusted_Connection=false",
    "MultipleActiveResultSets=true",
    "Trust Server Certificate=true",
  ].filter((part): part is string => !!part);

  return parts.join("; ");
};

const isMultiTenantV1 = (): boolean =>
  (getConfigValue("REACT_APP_MULTI_TENANT_VERSION", process.env.REACT_APP_MULTI_TENANT_VERSION) ?? "2") === "1";

const toApiOrganisationsV2 = (organisations: MultiTenantConfigOrganisation[]): AnyRecord[] =>
  organisations.map((org) => ({
    organisationName: org.organisationName,
    namedConnectionSettings: (org.namedConnectionSettings ?? []).map((setting) => ({
      name: setting.name,
      settings: toApiDatabaseConnection(setting.settings),
    })),
    namedElasticSettings: (org.namedElasticSettings ?? []).map((setting) => ({
      name: setting.name,
      settings: toApiIndexConnection(setting.settings),
    })),
  }));

/** v1 API expects flat org connection fields, not named settings. */
const toApiOrganisationsV1 = (organisations: MultiTenantConfigOrganisation[]): AnyRecord[] =>
  organisations.map((org) => {
    const connection = org.namedConnectionSettings?.[0]?.settings;
    const elastic = org.namedElasticSettings?.[0]?.settings;

    return {
      organisationName: org.organisationName,
      symphonyConnectionString: buildConnectionString(connection),
      elasticAlias: elastic?.alias ?? "",
      elasticNodes: elastic?.elasticNodes ?? [],
    };
  });

const toApiOrganisations = (organisations: MultiTenantConfigOrganisation[]): AnyRecord[] =>
  isMultiTenantV1() ? toApiOrganisationsV1(organisations) : toApiOrganisationsV2(organisations);

/** Build the create payload expected by POST /MultiTenantConfig. */
export const toCreateConfigPayload = (doc: MultiTenantConfigGetDto): MultiTenantConfigPostDto => ({
  name: doc.name,
  appName: doc.appName,
  organisations: toApiOrganisations(doc.organisations) as unknown as MultiTenantConfigOrganisation[],
});

/** Build the update payload expected by PUT /MultiTenantConfig. */
export const toUpdateConfigPayload = (doc: MultiTenantConfigGetDto): MultiTenantConfigPutDto => ({
  id: doc.id,
  name: doc.name,
  appName: doc.appName,
  organisations: toApiOrganisations(doc.organisations) as unknown as MultiTenantConfigOrganisation[],
});
